/**
 * The sentences of the captain card (R1118, R1889, R2087, numbers.md §12).
 * The career arc of the age and the rules of training and practice are
 * facts of the CAPTAIN fold (R1895, R2087, `howfold.ts`).
 *
 * The server ships every figure: `Captain.effects` is what the points a
 * captain holds are worth now, and `CatalogRules.captain` is the rate of
 * one point and the career arc. These functions pick the sentence and
 * print the figure; they compute no bonus of their own.
 */
import {
  type Captain, type CaptainEffects, type CaptainRules, CaptainPhase,
} from '../../gen/proto/aquaphobia';
import { LOCALE, overlay as S } from '../../gen/strings';
import { clamp01 } from '../../render/math';

/** One percent figure as the card prints it: at most one decimal, in the
 * picked language's decimal mark, never grouped (the figures stay small). */
const FIG = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1, useGrouping: false });
export const pctNum = (n: number): string => FIG.format(n);

/** What the points one captain holds in attribute `attr` are worth now. */
export function captainEffectText(attr: number, e: CaptainEffects): string {
  switch (attr) {
    case 0: return S.cap_eff_gunnery({ pct: pctNum(e.damagePct) });
    case 1: return S.cap_eff_helm({ pct: pctNum(e.speedPct) });
    case 2: return S.cap_eff_silent({ pct: pctNum(e.noiseCutPct) });
    case 3: return S.cap_eff_engineering({ pct: pctNum(e.regenPct) });
    case 4: return S.cap_eff_command({ pct: pctNum(e.floorLiftPct) });
    case 5: return S.cap_eff_prospecting({
      pct: pctNum(e.diveYieldPct), cut: pctNum(e.injuryCutPct),
    });
    default: return '';
  }
}

/** What one point of attribute `attr` is worth: a One point row of the
 *  HOW IT WORKS fold of CAPTAIN (R2087). */
export function perPointText(attr: number, r: CaptainRules): string {
  const pct = pctNum(r.perPointPct[attr] ?? 0);
  switch (attr) {
    case 0: return S.capsum_gunnery({ pct });
    case 1: return S.capsum_helm({ pct });
    case 2: return S.capsum_silent({ pct, max: pctNum(r.silentMaxCutPct) });
    case 3: return S.capsum_engineering({ pct });
    case 4: return S.capsum_command({ pct });
    case 5: return S.capsum_prospecting({ pct, cut: pctNum(r.injuryCutPerPointPct) });
    default: return '';
  }
}

/** The measure each attribute practises with, in the server's attribute
 *  order (numbers.md §12, R1478). Built once at load: the language is
 *  picked once (R855). */
const PRACTICE: readonly string[] = [
  S.cap_practice_gunnery, S.cap_practice_helm, S.cap_practice_silent,
  S.cap_practice_engineering, S.cap_practice_command, S.cap_practice_prospecting,
];

/** The measure one attribute practises with: a One point row of the HOW
 *  IT WORKS fold of CAPTAIN (R2087). */
export function practiceText(attr: number): string {
  return PRACTICE[attr] ?? '';
}

/** The decline rate of practice, from the rules: the Practice fact of a
 *  captain in decline (R2087). */
export function practiceDeclineText(r: CaptainRules): string {
  return S.capinfo_practice_decline({ pct: pctNum(r.practiceDeclinePct) });
}

/** The share of the next practice point in `attr`, as a whole percent
 *  from 0 to 100 (R1478). The server ships the share; a cadet and an old
 *  server ship none, which reads as 0. */
export function practicePct(c: Captain, attr: number): number {
  const v = c.practice[attr] ?? 0;
  return Math.round(clamp01(v) * 100);
}

/** The practice points of this season in `attr`. */
export function practisedPoints(c: Captain, attr: number): number {
  return c.practised[attr] ?? 0;
}

/** One attribute of one captain on the captain card (R1482, R1889): the value
 *  against `attr_train_cap`, the practice share and the season count
 *  against `practice_season_cap`. Both caps are the rules'; a cap of 0
 *  (no rules yet) reads as "no cap known". */
interface AttrTrainFigures {
  value: number;
  cap: number;
  /** The attribute stands at `attr_train_cap`: no training, no practice. */
  atCap: boolean;
  /** This season's practice points reached `practice_season_cap`. */
  seasonFull: boolean;
  pct: number;
  practised: number;
  seasonCap: number;
}

export function attrTrainFigures(c: Captain, attr: number, r: CaptainRules | undefined): AttrTrainFigures {
  const value = c.attrs[attr] ?? 0;
  const cap = r?.attrTrainCap ?? 0;
  const seasonCap = r?.practiceSeasonCap ?? 0;
  const practised = practisedPoints(c, attr);
  return {
    value, cap, practised, seasonCap,
    atCap: cap > 0 && value >= cap,
    seasonFull: seasonCap > 0 && practised >= seasonCap,
    pct: practicePct(c, attr),
  };
}

/** The name and the value against the cap, `Gunnery: 6 / 10`: the name of
 *  the pips of a card row (R2087). */
export function attrValueText(f: AttrTrainFigures, attr: string): string {
  return S.captrain_value({ attr, v: f.value, cap: f.cap });
}

/** What stands in place of the practice figure of the dive start sheet,
 *  or '' when the figure draws: the attribute cap first, then the season
 *  cap (numbers.md §12). */
export function practiceStopText(f: AttrTrainFigures): string {
  if (f.atCap) return S.captrain_at_cap;
  if (f.seasonFull) return S.captrain_season_full({ cap: f.seasonCap });
  return '';
}

/** The short word in place of the practice percent of a card row, or ''
 *  (round 100 card 01, R2087): MAX at the attribute cap, then FULL at the
 *  season cap. */
export function practiceStopWord(f: AttrTrainFigures): string {
  if (f.atCap) return S.captrain_max;
  if (f.seasonFull) return S.captrain_full;
  return '';
}

/** The career line: the phase and the seasons to the next one. The server
 * ships the phase and the countdown from the age arc (R1170), so the
 * sentence speaks the reader's language and the client computes nothing. */
export function careerText(c: Captain): string {
  switch (c.phaseKind) {
    case CaptainPhase.CAPTAIN_PHASE_DECLINE: return S.capinfo_decline;
    case CaptainPhase.CAPTAIN_PHASE_PEAK: return S.capinfo_peak({ n: c.seasonsToNextPhase });
    default: return S.capinfo_rise({ n: c.seasonsToNextPhase });
  }
}
